import {
  LayoutDashboard,
  BookOpen,
  BarChart3,
  Timer,
  Wallet,
  Sparkles,
  GraduationCap,
  Building2,
  Brain,
  FileText,
  LayoutGrid,
  Eye,
  type LucideIcon,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import type { Translations } from "../../i18n/translations";

/**
 * Two-level navigation model.
 *
 * Level 1 (PrimaryRail): the four modules.
 * Level 2 (SecondarySidebar): entries belonging to the open module.
 *
 * Nothing here reads the router — the config is data, so it can be unit-tested and
 * reused by both the desktop rail and the mobile burger.
 */

export type ModuleId = "dashboard" | "edgy" | "mentor" | "propfirm";

export type SecondaryEntry = {
  id: string;
  label: string;
  to: string;
  icon?: LucideIcon;
};

export type SecondaryGroup = {
  id: string;
  label: string;
  icon?: LucideIcon;
  /** Rendered collapsed by default — for entries that are rarely used. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  entries: SecondaryEntry[];
};

export type ModuleDefinition = {
  id: ModuleId;
  label: string;
  icon: LucideIcon;
  /** Where the rail icon navigates when the module has no remembered tab. */
  defaultPath: string;
  /** Modules without a secondary sidebar render full-width (V1: mentor, propfirm). */
  entries?: SecondaryEntry[];
  groups?: SecondaryGroup[];
  /**
   * Plan-gated modules stay VISIBLE but locked, so the upgrade path is discoverable
   * rather than hidden (product decision).
   */
  requiresPlan?: boolean;
};

const MENTOR_PLANS = new Set(["pro", "proAnnual", "max", "maxAnnual"]);

export function canAccessMentor(plan: string | null | undefined): boolean {
  return plan != null && MENTOR_PLANS.has(plan);
}

export function getModules(t: Translations): ModuleDefinition[] {
  return [
    {
      id: "dashboard",
      label: t.nav.dashboard,
      icon: LayoutDashboard,
      defaultPath: "/dashboard",
      entries: [
        { id: "overview", label: t.newNav.dashboard.overview, to: "/dashboard", icon: LayoutDashboard },
        { id: "journal", label: t.nav.journal, to: "/trading-journal", icon: BookOpen },
        { id: "analytics", label: t.nav.analytics, to: "/trading-analytics", icon: BarChart3 },
        { id: "sessions", label: t.nav.sessions, to: "/trading-sessions", icon: Timer },
        // Playbook is deliberately reachable from both Dashboard and Edgy: it is the
        // trader's strategy, used as reference material and as Edgy's input.
        { id: "playbook", label: t.playbook.title, to: "/playbook", icon: Sparkles },
        { id: "accounts", label: t.nav.accounts, to: "/trading-accounts", icon: Wallet },
      ],
      groups: [
        {
          id: "more",
          label: t.newNav.more,
          collapsible: true,
          defaultOpen: false,
          entries: [
            { id: "discipline", label: t.nav.disciplineManager, to: "/discipline-manager", icon: Brain },
            { id: "reports", label: t.nav.reports, to: "/reports", icon: FileText },
          ],
        },
      ],
    },
    {
      id: "propfirm",
      label: t.nav.propFirmManager,
      icon: Building2,
      defaultPath: "/prop-firm-manager",
      // No secondary sidebar in V1.
    },
    {
      id: "edgy",
      label: t.edgy.drawerTitle,
      icon: Sparkles,
      defaultPath: "/my-analyses",
      entries: [
        { id: "analyses", label: t.edgy.history.navLabel, to: "/my-analyses", icon: Sparkles },
        { id: "playbook", label: t.playbook.title, to: "/playbook", icon: BookOpen },
      ],
    },
    {
      id: "mentor",
      label: t.nav.mentor,
      icon: GraduationCap,
      defaultPath: "/mentor",
      requiresPlan: true,
      // Only the student list is static here. Once a student is open, the sidebar
      // appends that student's pages (see buildStudentEntries) — those depend on
      // the :studentId in the URL and on per-relation permissions.
      entries: [{ id: "students", label: t.mentor.myStudents, to: "/mentor", icon: GraduationCap }],
    },
  ];
}

export type StudentNavPermissions = {
  canViewJournal: boolean;
  canViewDashboard: boolean;
  canViewAnalytics: boolean;
  canViewSessions: boolean;
  canViewPropFirmManager: boolean;
};

/** Deny-by-default: a relation with no permissions row exposes nothing. */
export const NO_STUDENT_PERMISSIONS: StudentNavPermissions = {
  canViewJournal: false,
  canViewDashboard: false,
  canViewAnalytics: false,
  canViewSessions: false,
  canViewPropFirmManager: false,
};

/** Reads "/mentor/student/<id>/..." — the mentor sidebar is student-scoped. */
export function studentIdFromPath(pathname: string): string | null {
  const match = /^\/mentor\/student\/([^/]+)/.exec(pathname);
  return match ? match[1] : null;
}

/**
 * The student pages the mentor may open, built at render time: the destinations
 * depend on which student is open and on the permissions granted per relation,
 * so they cannot be static config. Mirrors the tab list StudentLayout used to
 * render horizontally — permission filtering included, so a revoked section is
 * absent from the sidebar rather than linking to a page that refuses to load.
 */
export function buildStudentEntries(
  t: Translations,
  studentId: string,
  permissions: StudentNavPermissions
): SecondaryEntry[] {
  const base = `/mentor/student/${studentId}`;

  return [
    { id: "overview", label: t.mentor.studentView, to: base, icon: Eye, enabled: true },
    { id: "dashboard", label: t.mentor.canViewDashboard, to: `${base}/dashboard`, icon: LayoutDashboard, enabled: permissions.canViewDashboard },
    { id: "journal", label: t.mentor.canViewJournal, to: `${base}/journal`, icon: BookOpen, enabled: permissions.canViewJournal },
    { id: "analytics", label: t.mentor.canViewAnalytics, to: `${base}/analytics`, icon: BarChart3, enabled: permissions.canViewAnalytics },
    { id: "sessions", label: t.mentor.canViewSessions, to: `${base}/sessions`, icon: Timer, enabled: permissions.canViewSessions },
    { id: "propfirm", label: t.mentor.canViewPropFirmManager, to: `${base}/propfirm`, icon: Building2, enabled: permissions.canViewPropFirmManager },
  ]
    .filter((entry) => entry.enabled)
    .map(({ enabled: _enabled, ...entry }) => entry);
}

/** The "My pages" group is built at render time from the user's custom pages. */
export function buildCustomPagesGroup(
  t: Translations,
  customPages: Array<{ id: string; name: string; icon?: string }>
): SecondaryGroup {
  return {
    id: "custom-pages",
    label: t.newNav.myPages,
    icon: LayoutGrid,
    collapsible: true,
    defaultOpen: true,
    // The user's chosen Lucide icon is resolved here rather than dropped: collapsed,
    // the icon is the only thing distinguishing one custom page from another.
    entries: customPages.map((page) => ({
      id: page.id,
      label: page.name,
      to: `/p/${page.id}`,
      icon: (page.icon ? (LucideIcons as any)[page.icon] : undefined) ?? LayoutGrid,
    })),
  };
}

/** Which module a path belongs to — drives the active rail icon. */
export function moduleForPath(pathname: string, modules: ModuleDefinition[]): ModuleId {
  for (const module of modules) {
    const paths = [
      ...(module.entries?.map((e) => e.to) ?? []),
      ...(module.groups?.flatMap((g) => g.entries.map((e) => e.to)) ?? []),
      module.defaultPath,
    ];
    // Longest match wins so /my-analyses does not resolve to "/".
    if (paths.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
      return module.id;
    }
  }
  // Custom pages live under the Dashboard module.
  if (pathname.startsWith("/p/")) return "dashboard";
  return "dashboard";
}

const STORAGE_KEY = "edgely:nav:lastTab";

/** Remembers the last entry visited per module, so returning restores context. */
export function rememberTab(moduleId: ModuleId, path: string): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    map[moduleId] = path;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Storage can be unavailable (private mode, quota) — navigation must not break.
  }
}

export function getRememberedTab(moduleId: ModuleId): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw) as Record<string, string>;
    return map[moduleId] ?? null;
  } catch {
    return null;
  }
}
